import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Combobox, useCombobox } from 'src/design/atoms/Combobox';
import { InputBase } from 'src/design/atoms/InputBase';
import { IconSearch } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { PostLinkItemDto } from 'src/api/FiretrolApiClient';
import { usePostLinkOptions } from 'src/api/queries/posts/usePostOptions';
import useDebounce from 'src/hooks/useDebounce';

interface PostLinkPickerProps {
  jobId: number;
  onLinkSelected: (option: PostLinkItemDto) => void;
}

export const PostLinkPicker: React.FC<PostLinkPickerProps> = ({
  jobId,
  onLinkSelected,
}) => {
  const [query, setQuery] = React.useState('');
  const debouncedTitle = useDebounce(query, 300);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const { data, isFetching: loading } = usePostLinkOptions(
    jobId,
    debouncedTitle,
  );
  const options: PostLinkItemDto[] = React.useMemo(() => data ?? [], [data]);

  const groupedData = useMemo(() => {
    const groups = options.reduce((acc, option) => {
      const group = option.resource;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push({
        value: `${group}:${option.id}`,
        label: option.label.length > 0 ? option.label : 'Untitled',
        original: option,
      });
      return acc;
    }, {} as Record<string, { value: string; label: string; original: PostLinkItemDto }[]>);

    return Object.entries(groups).map(([group, items]) => ({
      group,
      items,
    }));
  }, [options]);

  return (
    <Paper withBorder={false} h={215} p="md">
      <Combobox
        store={combobox}
        withinPortal={false}
        onOptionSubmit={(value) => {
          const [resource, id] = value.split(':');
          const group = groupedData.find((g) => g.group === resource);
          const item = group?.items.find((i) => i.value === value);
          if (item?.original) {
            onLinkSelected(item.original);
          }
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            rightSection={
              loading ? <Loader size="xs" /> : <IconSearch size={16} />
            }
            value={query}
            onChange={(event) => {
              combobox.openDropdown();
              combobox.updateSelectedOptionIndex();
              setQuery(event.currentTarget.value);
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => {
              combobox.closeDropdown();
              setQuery('');
            }}
            placeholder="Search for items to link..."
            rightSectionPointerEvents="none"
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {groupedData.length > 0 ? (
              groupedData.map((group) => (
                <Combobox.Group key={group.group} label={group.group}>
                  {group.items.map((item) => (
                    <Combobox.Option key={item.value} value={item.value}>
                      {item.label}
                    </Combobox.Option>
                  ))}
                </Combobox.Group>
              ))
            ) : (
              <Combobox.Empty>Type to find items to link...</Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Paper>
  );
};
